/* eslint-disable @typescript-eslint/no-empty-function */
import { Link } from 'react-router-dom-6';

import VaccinationDetail from './VaccinationDetail';
import styles from './Vaccinations.module.less';

import {
    Alert,
    ApplePassButton,
    Button,
    Card,
    Divider,
    GooglePassButton
} from '$HVUI';
import { flexibleDateString, fullNameFromProfile } from '$Utils';

const vaccinationUploadCTABlock = firstDoseID => {
    //The backend requires the first vaccination ID in the url path
    if (!firstDoseID) return;
    const path = `/vaccination-upload/single_dose/${firstDoseID}/0/0/0/agreement`;
    const message = (
        <div style={{ textAlign: 'center' }}>
            Have you received all doses of your COVID-19 vaccination yet?
            Complete your digital vaccination record by uploading an image of
            your CDC Card.
            <br />
            <Link key={`upload-dose`} to={path}>
                <Button
                    style={{ marginTop: '16px' }}
                    // Don't need onClick, we're inside a Link
                    onClick={() => {}}
                    id="upload-vaccination"
                    level="primary"
                    size="narrow"
                    text="Upload 2nd dose"
                />
            </Link>
        </div>
    );
    return (
        <>
            <Divider className={styles['divider-card']} />
            <Alert type="info" message={message} />
        </>
    );
};

const VaccinationCourse = props => {
    const {
        conditionLabel,
        conditionSlug,
        context,
        isLoggedIn,
        me,
        vaccinations
    } = props;

    if (!vaccinations || vaccinations.length == 0) return null;

    const firstDoseID = vaccinations[0].id;

    //Because Apple pass and Google pass don't always exist for *all* of the
    //  vaccinations, iterate through them to find valid UUIDs.
    const applePassUUID = vaccinations
        .filter(v => v.vaccine.condition_label === conditionLabel)
        .find(vaccination => {
            const shouldShow = vaccination.show_apple_pass_download_link;
            const hasUUID = !!vaccination.apple_pass_public_uuid;
            return shouldShow && hasUUID;
        })?.apple_pass_public_uuid;
    const googlePassUUID = vaccinations
        .filter(v => v.vaccine.condition_label === conditionLabel)
        .find(vaccination => {
            const shouldShow = vaccination.show_google_pass_download_link;
            const hasUUID = !!vaccination.google_pass_public_uuid;
            return shouldShow && hasUUID;
        })?.google_pass_public_uuid;

    const uploadFlow = context?.vaccination_upload_flow;
    const isSupportedFlow = ['single_dose', 'multi_dose'].includes(uploadFlow);
    const uploadEligible =
        isLoggedIn && isSupportedFlow && conditionSlug === 'covid19';

    return (
        <Card className={styles['card-rounded']} key="vaccination-card">
            <h3 className={styles['vaccine-type-label']}>
                {conditionLabel.toLowerCase().includes('monkeypox')
                    ? 'Mpox Vaccine'
                    : conditionLabel}
            </h3>
            <div className={styles['print-label']}>
                {fullNameFromProfile(me)}
            </div>
            <div className={styles['print-label']}>
                Date of birth: {flexibleDateString(me.birthdate, null, 'MES')}
            </div>

            {vaccinations
                .filter(v => v.vaccine.condition_label === conditionLabel)
                // re-sort vaccinations within VaccinationDetail oldest to newest, to keep dose order correct
                .sort(
                    (a, b) =>
                        new Date(a.vaccination_administration_datetime) -
                        new Date(b.vaccination_administration_datetime)
                )
                .map((vaccination, index) => (
                    <VaccinationDetail
                        key={vaccination.id}
                        me={me}
                        index={index}
                        vaccination={vaccination}
                        firstDoseID={firstDoseID}
                        isLoggedIn={isLoggedIn}
                    />
                ))}
            {(applePassUUID || googlePassUUID) && (
                <div className={styles['button-container']}>
                    <Divider className={styles['divider-card']} />
                    <span className={styles['download-reminder-text']}>
                        Download your digital vaccination record to your mobile
                        phone for your convenience!
                    </span>
                    <div className={styles['wallet-container']}>
                        {applePassUUID && (
                            <ApplePassButton
                                key="apple-pass-button"
                                public_uuid={applePassUUID}
                                first_name={me.first_name}
                                last_name={me.last_name}
                                birthdate={me.birthdate}
                                queryType={isLoggedIn ? 'patient' : 'token'}
                            />
                        )}
                        {googlePassUUID && (
                            <GooglePassButton
                                key="google-pass-button"
                                public_uuid={googlePassUUID}
                                first_name={me.first_name}
                                last_name={me.last_name}
                                birthdate={me.birthdate}
                                queryType={isLoggedIn ? 'patient' : 'token'}
                            />
                        )}
                    </div>
                    <p className={styles['help-text']}>
                        If you're experiencing difficulty downloading your
                        record, please{' '}
                        <a
                            href="https://healthvana.com/faq/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            visit our FAQ page
                        </a>{' '}
                        for more information.
                    </p>
                    {uploadEligible && vaccinationUploadCTABlock(firstDoseID)}
                </div>
            )}
        </Card>
    );
};

export default VaccinationCourse;
